/* eslint-disable no-unused-vars */
// src/components/AdminTroubleshooting.js
import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Modal, Badge, Alert, Spinner, Table, Row, Col, InputGroup } from 'react-bootstrap';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function AdminTroubleshooting() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Form states
  const [responseForm, setResponseForm] = useState({
    response: ''
  });

  // Load tickets on component mount
  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/admin/tickets');
      console.log('Admin Tickets API response:', response.data);
      // Sort tickets by latest first
      const sortedTickets = Array.isArray(response.data) 
        ? response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        : [];
      setTickets(sortedTickets);
    } catch (error) {
      console.error('Error loading tickets:', error);
      if (error.response?.status === 401) {
        toast.error('Please log in to view tickets');
      } else if (error.response?.status === 403) {
        toast.error('Access denied - Admin privileges required');
      } else {
        toast.error('Failed to load tickets');
      }
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`/admin/tickets/${selectedTicket._id}/respond`, {
        response: responseForm.response
      });
      
      // Update the ticket in the list
      setTickets(tickets.map(ticket => 
        ticket._id === selectedTicket._id ? response.data : ticket
      ));
      
      setShowResponseModal(false);
      setSelectedTicket(null);
      setResponseForm({ response: '' });
      toast.success('Response submitted successfully');
    } catch (error) {
      console.error('Error submitting response:', error);
      toast.error('Failed to submit response');
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      const response = await axios.put(`/admin/tickets/${ticketId}/status`, {
        status: newStatus
      });
      
      setTickets(tickets.map(ticket => 
        ticket._id === ticketId ? response.data : ticket
      ));
      
      toast.success(`Ticket status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update ticket status');
    }
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      low: 'secondary',
      medium: 'warning',
      high: 'danger',
      urgent: 'dark'
    };
    return <Badge bg={variants[priority]}>{priority.toUpperCase()}</Badge>;
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: 'danger',
      'in-progress': 'warning',
      resolved: 'success',
      closed: 'secondary'
    };
    return <Badge bg={variants[status]}>{status.replace('-', ' ').toUpperCase()}</Badge>;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      general: '❓',
      technical: '🔧',
      billing: '💰',
      account: '👤',
      booking: '📅',
      payment: '💳'
    };
    return icons[category] || '❓';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter tickets based on search and filters
  const filteredTickets = tickets.filter(ticket => {
    const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || ticket.priority === filterPriority;
    const matchesSearch = searchTerm === '' || 
      ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.userName.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesStatus && matchesPriority && matchesSearch;
  });

  // Statistics
  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in-progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    closed: tickets.filter(t => t.status === 'closed').length,
    urgent: tickets.filter(t => t.priority === 'urgent').length
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Header */}
      <div className="sticky-top bg-white border-bottom pb-3" style={{ 
        zIndex: 1020,
        top: '56px'
      }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h2 className="mb-0">Admin Troubleshooting</h2>
            <p className="text-muted mb-0">Manage all driver support tickets</p>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={loadTickets}>
              Refresh
            </Button>
            <Badge bg="info" className="fs-6">
              {filteredTickets.length} of {tickets.length} Tickets
            </Badge>
          </div>
        </div>

        {/* Statistics */}
        <Row className="mb-3">
          <Col md={2}>
            <Card className="text-center">
              <Card.Body className="py-2">
                <h6 className="mb-0">{stats.total}</h6>
                <small className="text-muted">Total</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="text-center border-danger">
              <Card.Body className="py-2">
                <h6 className="mb-0 text-danger">{stats.open}</h6>
                <small className="text-muted">Open</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="text-center border-warning">
              <Card.Body className="py-2">
                <h6 className="mb-0 text-warning">{stats.inProgress}</h6>
                <small className="text-muted">In Progress</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="text-center border-success">
              <Card.Body className="py-2">
                <h6 className="mb-0 text-success">{stats.resolved}</h6>
                <small className="text-muted">Resolved</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="text-center border-secondary">
              <Card.Body className="py-2">
                <h6 className="mb-0 text-secondary">{stats.closed}</h6>
                <small className="text-muted">Closed</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="text-center border-dark">
              <Card.Body className="py-2">
                <h6 className="mb-0 text-dark">{stats.urgent}</h6>
                <small className="text-muted">Urgent</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Row className="mb-3">
          <Col md={4}>
            <div className="position-relative">
              <Form.Control
                type="text"
                placeholder="Search tickets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ paddingRight: '2rem' }}
              />
              {searchTerm && (
                <Button
                  variant="link"
                  className="position-absolute top-50 end-0 translate-middle-y text-muted pe-3"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                  tabIndex={0}
                  style={{ fontSize: '1.5rem', lineHeight: 1, textDecoration: 'none' }}
                >
                  ×
                </Button>
              )}
            </div>
          </Col>
          <Col md={3}>
            <Form.Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="open">Open</option>
              <option value="in-progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
            >
              <option value="all">All Priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </Form.Select>
          </Col>
          <Col md={2}>
            <Button 
              variant="outline-secondary" 
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('all');
                setFilterPriority('all');
              }}
            >
              Clear
            </Button>
          </Col>
        </Row>
      </div>

      <div className="row mt-4">
        <div className="col-12">
          {filteredTickets.length === 0 ? (
            <Card>
              <Card.Body className="text-center py-5">
                <div className="mb-3">
                  <span className="fs-1">🎫</span>
                </div>
                <h4>No tickets found</h4>
                <p className="text-muted">
                  {tickets.length === 0 
                    ? 'No support tickets have been created yet.'
                    : 'No tickets match your current filters.'
                  }
                </p>
              </Card.Body>
            </Card>
          ) : (
            <div className="row">
              {filteredTickets.map((ticket) => (
                <div key={ticket._id} className="col-md-6 col-lg-4 mb-3">
                  <Card className="h-100">
                    <Card.Header className="d-flex justify-content-between align-items-start">
                      <div className="d-flex align-items-center">
                        <span className="me-2 fs-5">{getCategoryIcon(ticket.category)}</span>
                        <div>
                          <h6 className="mb-0">{ticket.title}</h6>
                          <small className="text-muted">#{ticket._id.slice(-6)}</small>
                        </div>
                      </div>
                      <div className="text-end">
                        {getPriorityBadge(ticket.priority)}
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-2">
                        <p className="text-muted small mb-1">
                          {ticket.description.length > 100 
                            ? `${ticket.description.substring(0, 100)}...` 
                            : ticket.description
                          }
                        </p>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted">
                          Created: {formatDate(ticket.createdAt)}
                        </small>
                        {getStatusBadge(ticket.status)}
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted">
                          By: {ticket.userName || 'Unknown User'}
                        </small>
                      </div>
                      <div className="d-flex gap-1 flex-wrap">
                        {ticket.status !== 'closed' && (
                          <Button 
                            size="sm" 
                            variant="outline-primary"
                            onClick={() => {
                              setSelectedTicket(ticket);
                              setShowResponseModal(true);
                            }}
                          >
                            Respond
                          </Button>
                        )}
                        {ticket.status === 'open' && (
                          <Button 
                            size="sm" 
                            variant="outline-warning"
                            onClick={() => handleStatusChange(ticket._id, 'in-progress')}
                          >
                            Start Progress
                          </Button>
                        )}
                        {ticket.status === 'in-progress' && (
                          <Button 
                            size="sm" 
                            variant="outline-success"
                            onClick={() => handleStatusChange(ticket._id, 'resolved')}
                          >
                            Mark Resolved
                          </Button>
                        )}
                        {ticket.status === 'resolved' && (
                          <Button 
                            size="sm" 
                            variant="outline-secondary"
                            onClick={() => handleStatusChange(ticket._id, 'closed')}
                          >
                            Close
                          </Button>
                        )}
                        {ticket.status !== 'closed' && (
                          <Button 
                            size="sm" 
                            variant="outline-secondary"
                            onClick={() => handleStatusChange(ticket._id, 'closed')}
                          >
                            Close
                          </Button>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Response Modal */}
      <Modal show={showResponseModal} onHide={() => setShowResponseModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Respond to Ticket</Modal.Title>
        </Modal.Header>
        {selectedTicket && (
          <>
            <Modal.Body>
              <div className="mb-4">
                <h5>{selectedTicket.title}</h5>
                <div className="d-flex gap-2 mb-2">
                  {getPriorityBadge(selectedTicket.priority)}
                  {getStatusBadge(selectedTicket.status)}
                </div>
                <p className="text-muted small">
                  Created by {selectedTicket.userName} on {formatDate(selectedTicket.createdAt)}
                </p>
                <div className="bg-light p-3 rounded">
                  <strong>Issue Description:</strong>
                  <p className="mb-0 mt-2">{selectedTicket.description}</p>
                </div>
              </div>

              {selectedTicket.responses && selectedTicket.responses.length > 0 && (
                <div className="mb-4">
                  <h6>Previous Responses:</h6>
                  {selectedTicket.responses.map((response, index) => (
                    <div key={index} className="border-start border-primary ps-3 mb-3">
                      <div className="d-flex justify-content-between">
                        <small className="text-muted">
                          {response.adminName || 'Admin'} - {formatDate(response.createdAt)}
                        </small>
                      </div>
                      <p className="mb-0 mt-1">{response.response}</p>
                    </div>
                  ))}
                </div>
              )}

              <Form onSubmit={handleSubmitResponse}>
                <Form.Group>
                  <Form.Label>Your Response *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={responseForm.response}
                    onChange={(e) => setResponseForm({...responseForm, response: e.target.value})}
                    placeholder="Enter your response to the driver..."
                    required
                  />
                </Form.Group>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowResponseModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSubmitResponse}>
                Submit Response
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      <ToastContainer position="top-right" />
    </div>
  );
} 